<script setup lang="ts" generic="T">
const props = defineProps({
  options: {
    type: [Array<T>],
    default() {
      return [];
    },
  },
  label: {
    type: String,
  },
  modelValue: {
    type: null,
  },
  searchable: {
    type: Boolean,
    default: false,
  },
  placeholder: {
    type: String,
    default: "Choisissez une option",
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  searchField: {
    type: Function,
  },
  maxHeight: {
    type: String,
    default: "100%",
  },
  showEmptyMsg: {
    type: Boolean,
  },
  error: {
    type: Boolean,
    default: false,
  },
  limiter: {
    type: Number,
    default: null,
  },
  maxElementToShow: {
    // number of options to display without scrolling
    type: Number,
    required: false,
  },
  defaultValue: {
    type: String,
    require: false,
  },
});

const emit = defineEmits(["update:modelValue", "clickAdd", "searchValue"]);

const search = ref<string>("");
const toggled = ref<boolean>(false);

const inputRef = ref<HTMLInputElement | null>(null);
const dropdownRef = ref<HTMLDivElement | null>(null);

const arrowsItem = ref(-1);
const ulContainer = ref<HTMLUListElement>();
const liItems = ref<HTMLLIElement[]>();
function untoggleDropdown(event: any) {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target)) {
    toggled.value = false;
  }
}

onMounted(() => {
  document.addEventListener("keydown", keyDownEvents);
  document.addEventListener("click", untoggleDropdown, true);
});

onUnmounted(() => {
  document.removeEventListener("keydown", keyDownEvents);
  document.removeEventListener("click", untoggleDropdown, true);
});

function keyDownEvents(e: KeyboardEvent) {
  if (toggled.value && ulContainer.value) {
    if (e.key === "ArrowUp") {
      e.preventDefault();
      arrowsItem.value = arrowsItem.value <= 0 ? searchOptions.value?.length - 1 : arrowsItem.value - 1;
    } else if (e.key == "ArrowDown") {
      e.preventDefault();
      arrowsItem.value = arrowsItem.value < searchOptions.value?.length - 1 ? arrowsItem.value + 1 : 0;
    } else if (e.key == "Enter") {
      pushSelectedValue(search.value);
      toggled.value = false;
    }
  }
}

function scrollToMatchingOption() {
  // get l'index dans la liste
  const index = searchOptions.value.findIndex((item: any) => {
    const itemValue = getDisplayedValue(item).toLowerCase();
    return itemValue.startsWith(search.value.toLowerCase());
  });

  if (index >= 0 && liItems.value && ulContainer.value) {
    const targetItem = liItems.value[index];
    const ul = ulContainer.value;

    if (targetItem) {
      // Position de l'élément cible par rapport au conteneur
      const targetOffsetTop = targetItem.offsetTop - ul.offsetTop;

      // Positionner directement le scroll du conteneur
      ul.scrollTop = targetOffsetTop;
    }
  }
}

watch(search, () => {
  nextTick(() => {
    scrollToMatchingOption();
  });
});

function getDisplayedValue(val: any) {
  if (props.label) {
    return val[props.label];
  } else {
    return val;
  }
}

function pushSelectedValue(val: any) {
  if (!props.disabled) {
    emit("update:modelValue", val as T);
  }
}

async function clickToggle() {
  if (!props.disabled) {
    toggled.value = !toggled.value;
    if (!search.value && props.defaultValue && !props.modelValue) {
      search.value = props.defaultValue;
      pushSelectedValue(props.defaultValue);
    } else {
      search.value = props.modelValue;
    }
    await nextTick();
    if (toggled.value) {
      inputRef.value?.focus();
    }
  }
}

function clickOption(item: string) {
  if (!props.disabled) {
    let val: any = null;

    val = item;
    search.value = item;

    if (val) {
      if (val === "00:00") {
        search.value = "";
        pushSelectedValue(null);
      } else {
        pushSelectedValue(val);
      }
    }
  }
  toggled.value = false;
}

const searchOptions = computed(() => {
  if (props.searchable && props.searchField && search.value?.length) {
    arrowsItem.value = -1;
    emit("searchValue", search.value);

    return props.options;
  } else {
    emit("searchValue", "");
    return props.options;
  }
});

const searchValue = (e: any) => {
  let value = e.target.value;

  let digits = value.replace(/\D/g, "");

  // Limite à 4 chiffres (HHMM)
  if (digits.length > 4) {
    digits = digits.slice(0, 4);
  }

  // Insère ':' après les deux premiers chiffres
  if (digits.length >= 3) {
    value = digits.slice(0, 2) + ":" + digits.slice(2);
  } else {
    value = digits;
  }
  // Émet la valeur formatée
  search.value = value;
};
</script>

<template>
  <div ref="dropdownRef" class="dropdown__container body_regular" :data-disabled="disabled" :data-error="props.error">
    <!-- START SELECT VALUE(S) -->
    <div
      class="dropdown__toggle sub"
      :data-toggled="
        (toggled && !Number.isFinite(props.limiter) && (showEmptyMsg || !!options.length)) ||
        (search.length >= props.limiter && Number.isFinite(props.limiter) && toggled)
      "
      @click.stop="clickToggle"
    >
      <div class="dropdown__selected" :data-searchable="searchable">
        <input
          v-if="toggled && searchable"
          ref="inputRef"
          v-model="search"
          :disabled="disabled"
          class="dropdown__input"
          type="text"
          @input="searchValue"
        />
        <div v-else class="dropdown__selected_item">
          <input
            v-if="!modelValue"
            :disabled="disabled"
            :readonly="disabled || !searchable || !toggled || !modelValue"
            :placeholder="placeholder"
            class="dropdown__input"
            type="text"
          />
          <slot v-else name="selected" :selected="modelValue">
            {{ getDisplayedValue(modelValue) }}
          </slot>
        </div>
      </div>
    </div>

    <!-- LIST -->
    <div
      class="dropdown__menu sub"
      :class="[
        (toggled && !Number.isFinite(props.limiter) && (showEmptyMsg || !!options.length)) ||
        (search.length >= props.limiter && Number.isFinite(props.limiter) && toggled)
          ? 'block'
          : 'hidden',
      ]"
    >
      <ul v-if="!disabled || (searchOptions?.length && !disabled)" ref="ulContainer" class="dropdown__options_max">
        <li
          v-for="(item, i) in searchOptions"
          :key="i"
          ref="liItems"
          class="dropdown__option h-[40px] px-[16px]"
          :data-arrow="arrowsItem === i"
          @click="clickOption(item as string)"
        >
          <span class="dropdown__option_item">
            <slot name="option" :option="item">
              {{ getDisplayedValue(item) }}
            </slot>
          </span>
        </li>
      </ul>
    </div>
  </div>
</template>

<style lang="scss" scoped></style>
